import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Eye, Download } from "lucide-react"
import { type ExamPaper } from "@/lib/api"
import { api } from "@/lib/api"

interface ExamPapersTableProps {
  papers: ExamPaper[]
  selectedPaperId?: number
  onView: (paper: ExamPaper) => void
  onEdit?: (paper: ExamPaper) => void
  onDelete?: (id: number) => void
}

export function ExamPapersTable({
  papers,
  selectedPaperId,
  onView,
  onEdit,
  onDelete,
}: ExamPapersTableProps) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[150px] sm:min-w-[200px]">标题</TableHead>
            <TableHead className="hidden w-20 sm:w-24 sm:table-cell">年份</TableHead>
            <TableHead className="w-20 sm:w-24">科目</TableHead>
            <TableHead className="hidden w-48 sm:w-64 lg:table-cell">标签</TableHead>
            <TableHead className="hidden w-20 sm:w-24 md:table-cell">查看</TableHead>
            <TableHead className="hidden w-20 sm:w-24 md:table-cell">下载</TableHead>
            <TableHead className="w-32 sm:w-36 text-right">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {papers.map((paper, index) => (
            <TableRow
              key={paper.id}
              className={`${index % 2 === 0 ? "bg-muted/30" : ""} ${
                selectedPaperId === paper.id ? "bg-primary/10 border-l-4 border-l-primary" : ""
              }`}
            >
              <TableCell className="font-medium py-4">
                <div className="max-w-[150px] sm:max-w-xs truncate text-xs sm:text-sm">
                  {paper.title}
                </div>
              </TableCell>
              <TableCell className="hidden py-4 text-xs text-muted-foreground sm:text-sm sm:table-cell">
                {paper.year}年
              </TableCell>
              <TableCell className="py-4 text-xs text-muted-foreground sm:text-sm">
                {paper.subject}
              </TableCell>
              <TableCell className="py-4 hidden lg:table-cell">
                <div className="flex flex-wrap gap-1">
                  {paper.tags?.slice(0, 3).map((tag, i) => (
                    <Badge
                      key={i}
                      variant="secondary"
                      className="bg-primary/10 text-xs text-primary hover:bg-primary/20"
                    >
                      {tag}
                    </Badge>
                  ))}
                  {paper.tags && paper.tags.length > 3 && (
                    <Badge variant="secondary" className="bg-muted text-xs text-muted-foreground">
                      +{paper.tags.length - 3}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="py-4 hidden text-xs text-muted-foreground sm:text-sm md:table-cell">
                <div className="flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  {paper.views || 0}
                </div>
              </TableCell>
              <TableCell className="py-4 hidden text-xs text-muted-foreground sm:text-sm md:table-cell">
                <div className="flex items-center gap-1">
                  <Download className="h-3 w-3" />
                  {paper.downloads || 0}
                </div>
              </TableCell>
              <TableCell className="py-4 text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="link"
                    className="h-auto p-0 text-xs text-primary sm:text-sm"
                    onClick={() => onView(paper)}
                  >
                    查看
                  </Button>
                  {paper.id && (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-xs text-blue-600 sm:text-sm hidden sm:block"
                      asChild
                    >
                      <a
                        href={api.getDownloadUrl(paper.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        下载
                      </a>
                    </Button>
                  )}
                  {onEdit && (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-xs text-blue-600 sm:text-sm"
                      onClick={() => onEdit(paper)}
                    >
                      编辑
                    </Button>
                  )}
                  {onDelete && paper.id && (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-xs text-destructive sm:text-sm"
                      onClick={() => paper.id && onDelete(paper.id)}
                    >
                      删除
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
